import { Button, Search } from '@ibsys/kit';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useFormGuardActions } from '@/entities/formGuard';
import { useHasPermission } from '@/entities/profile';
import { useDebouncedValue } from '@/shared/hooks';
import { useAppNavigate } from '@/shared/lib';
import { MasterDetailLayout } from '@/shared/ui';
import { EmployeeGroupsCard } from './ui/EmployeeGroupsCard/EmployeeGroupsCard';
import { EmployeeGroupsList } from './ui/EmployeeGroupsList/EmployeeGroupsList';

/**
 * Страница управления группами операторов: /employee-groups/:id?.
 * Двухпанельный макет: слева — список групп с поиском по названию,
 * справа — карточка выбранной/создаваемой группы (рендерится при наличии :id).
 * На узком экране панели показываются по очереди: сначала список, карточка — по выбору
 * группы или по кнопке добавления, с возвратом к списку.
 */
export const EmployeeGroupsPage: FC = () => {
  const { t } = useTranslation(['common', 'employees']);
  const { id: idOrCreating } = useParams<{ id?: string }>();
  const isCreating = idOrCreating === 'create';
  const id = idOrCreating && !isCreating ? +idOrCreating : undefined;

  const navigate = useAppNavigate();
  const canAdmin = useHasPermission('ADMIN_EMPLOYEE_GROUP');
  const { setIsDirty } = useFormGuardActions();

  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 300);

  const handleClickAdd = () => {
    navigate('/employee-groups/create');
  };

  const handleBack = () => {
    navigate('/employee-groups');
  };

  return (
    <MasterDetailLayout
      isDetailOpen={Boolean(idOrCreating)}
      onBack={handleBack}
      toolbar={
        <div className="flex w-full items-center gap-4">
          <Search
            className="flex-1"
            placeholder={t('employees:groups.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {canAdmin && (
            <Button onClick={handleClickAdd} className="ml-auto">
              {t('employees:groups.addGroup')}
            </Button>
          )}
        </div>
      }
      list={<EmployeeGroupsList searchQuery={debouncedSearchQuery} />}
      detail={
        <EmployeeGroupsCard
          groupId={id}
          isCreating={isCreating}
          key={id}
          onUnsavedChange={(dirty) => setIsDirty(dirty)}
        />
      }
      empty={
        <div className="flex items-center justify-center h-full text-muted-foreground">
          {t('employees:groups.selectGroup')}
        </div>
      }
    />
  );
};
